import { useApiCountryConfigurationNavigationRetrieve } from '@rover/rsdk/src/apiClient/latest/api-country-configuration-navigation-retrieve';
import { useApiFrontendConfigurationRetrieve } from '@rover/rsdk/src/apiClient/latest/api-frontend-configuration-retrieve';
import { useApiFrontendServiceTypesList } from '@rover/rsdk/src/apiClient/latest/api-frontend-service-types-list';
import type { CountryConfiguration } from '@rover/rsdk/src/apiClient/latest/model/countryConfiguration';
import type { FrontendConfiguration } from '@rover/rsdk/src/apiClient/latest/model/frontendConfiguration';
import type { PaginatedProviderRatingList } from '@rover/rsdk/src/apiClient/latest/model/paginatedProviderRatingList';
import type { PaginatedServiceTypeList } from '@rover/rsdk/src/apiClient/latest/model/paginatedServiceTypeList';
import { SeoLocationDescriptor } from '@rover/utilities/seo';

import { useApiSearchSeoLocationReviewsList } from './useSeoLocationHooks';

export type SeoLocationPageData = {
  reviews: PaginatedProviderRatingList | undefined;
  countryConfig: CountryConfiguration | undefined;
  frontendConfig: FrontendConfiguration | undefined;
  serviceTypes: PaginatedServiceTypeList | undefined;
  isLoading: boolean;
  isError: boolean;
  // Which of the queries above is loading or failed, for the Sentry report.
  queryTags: Record<string, boolean>;
};

/**
 * The queries every SEO location engine needs, whichever template it renders. This is the
 * runtime twin of the shared prefetch in `BaseSeoLocationContainer`: a query added to one
 * belongs in the other.
 */
const useSeoLocationPageData = (
  seoLocationDescriptor: SeoLocationDescriptor
): SeoLocationPageData => {
  const {
    isLoading: isLoadingReviews,
    isError: isErrorReviews,
    data: reviews,
  } = useApiSearchSeoLocationReviewsList(seoLocationDescriptor);
  const {
    isLoading: isLoadingCountryConfig,
    isError: isErrorCountryConfig,
    data: countryConfig,
  } = useApiCountryConfigurationNavigationRetrieve();
  const {
    isLoading: isLoadingFrontendConfig,
    isError: isErrorFrontendConfig,
    data: frontendConfig,
  } = useApiFrontendConfigurationRetrieve();
  const {
    isLoading: isLoadingServices,
    isError: isErrorServices,
    data: serviceTypes,
  } = useApiFrontendServiceTypesList();

  return {
    reviews,
    countryConfig,
    frontendConfig,
    serviceTypes,
    isLoading:
      isLoadingReviews || isLoadingCountryConfig || isLoadingFrontendConfig || isLoadingServices,
    isError: isErrorReviews || isErrorCountryConfig || isErrorFrontendConfig || isErrorServices,
    queryTags: {
      isLoadingReviews,
      isLoadingCountryConfig,
      isLoadingFrontendConfig,
      isLoadingServices,
      isErrorReviews,
      isErrorCountryConfig,
      isErrorFrontendConfig,
      isErrorServices,
    },
  };
};

export default useSeoLocationPageData;
